import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Flex, Form, Input, Modal, Popconfirm, Radio } from 'antd';
import styled from 'styled-components';

import {
  ActionBox,
  Divider,
  NestedBox,
  Space,
  Box,
  AnswerItem
} from '@/features/constructorCommon/styled';
import { QuestionList } from '@/features/constructorCommon/components/QuestionList';
import { QuestionnaireDependedAnswers, QuestionnaireQuestion } from '@/features/constructor';
import { ProductDependedAnswers, ProductQuestion } from '@/features/constructorProducts';
import { OPERATORS } from '@/utils/static';
import { ConstructorForm } from '@/components/admin/molecules';

export const Group = ({
  groupProp,
  sectionId,
  onCreated,
  handleUpdate,
  handleDelete,
  handleCreate,
  isCreating,
  isUpdating,
  isDeleting,
  type,
  dataSortedSections,
  loadingSections,
  dependentAnswers,
  setDependentAnswers,
  ...props
}) => {
  const { t } = useTranslation();
  const [group, setGroup] = useState(groupProp || {});
  const [dAnswers, setDAnswers] = useState([]);
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [isDepends, setIsDepends] = useState(false);

  useEffect(() => {
    if (!groupProp?.id || isModalOpened) return;

    setGroup(groupProp);
    if (groupProp?.visibleConditions && groupProp?.visibleConditions.length > 0) {
      setIsDepends(true);
    }
  }, [groupProp]);

  useEffect(() => {
    if (!dataSortedSections || !dependentAnswers || !dependentAnswers.length) return;

    setDAnswers(dependentAnswers);
  }, [dataSortedSections, dependentAnswers]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setGroup({ ...group, [name]: value });
  };

  const onChangeOperator = ({ target: { value } }) => {
    setGroup({ ...group, visibleOperator: value });
  };

  const onAddAnswers = (ans) => {
    setDAnswers(ans);

    setIsModalOpened(false);
  };

  const onChangeCheckbox = (e) => {
    const { name, checked } = e.target;
    setGroup({ ...group, [name]: checked });
  };

  const handleAction = () => {
    const dAnswersProp = isDepends
      ? dAnswers.length > 0
        ? dAnswers
        : !!groupProp?.visibleConditions?.length
          ? null
          : []
      : [];

    if (group.id) {
      handleUpdate({ group, dAnswersProp });
    } else {
      handleCreate({ group, sectionId, dAnswersProp }).then((res) => {
        setGroup({});
        onCreated && onCreated();
      });
    }
  };

  const onDelete = () => {
    handleDelete(group);
  };

  const isDisabled =
    (!group.id && !group.title) ||
    (group.title === groupProp?.section &&
      group.title_RU === groupProp?.section_RU &&
      group.title_LV === groupProp?.section_LV &&
      group.title === groupProp?.title &&
      group.title_RU === groupProp?.title_RU &&
      group.title_LV === groupProp?.title_LV &&
      dAnswers.length === 0 &&
      !!groupProp?.visibleConditions?.length === isDepends &&
      group.sortOrder === groupProp?.sortOrder &&
      group.isMultiple === groupProp?.isMultiple &&
      group.multipleMax === groupProp?.multipleMax &&
      group.multipleMin === groupProp?.multipleMin &&
      group.visibleOperator === groupProp?.visibleOperator &&
      group.addToPdf === groupProp?.addToPdf);

  return (
    <Box
      $isNew={!group.id}
      styles={{
        body: {
          padding: '16px'
        }
      }}
    >
      <ConstructorForm layout='vertical'>
        <Form.Item label={t('Group Title')}>
          <Input name='title' value={group.title} onChange={onChange} />
        </Form.Item>
        {type === 'products' && (
          <>
            <Form.Item label={`${t('Group Title')} (RU)`}>
              <Input name='title_RU' value={group.title_RU} onChange={onChange} />
            </Form.Item>
            <Form.Item label={`${t('Group Title')} (LV)`}>
              <Input name='title_LV' value={group.title_LV} onChange={onChange} />
            </Form.Item>
          </>
        )}
        {group.id && type === 'questionnaire' && (
          <Form.Item>
            <Checkbox name='addToPdf' checked={group.addToPdf} onChange={onChangeCheckbox}>
              {t('Add to PDF')}
            </Checkbox>
          </Form.Item>
        )}
        <Form.Item>
          <Checkbox
            name='isDepends'
            value={isDepends}
            checked={isDepends}
            onChange={(e) => {
              setIsDepends(e.target.checked);
              if (!e.target.checked) setDAnswers([]);
            }}
          >
            {t('This group depends on the answers')}
          </Checkbox>
        </Form.Item>

        {isDepends && (
          <>
            <div>
              {dAnswers.map((item, index) => (
                <AnswerItem key={`groupDependentAnswer${index}`}>{item.answer}</AnswerItem>
              ))}
            </div>
            <Button onClick={() => setIsModalOpened(true)} type='primary'>
              {group?.visibleConditions?.length
                ? 'Update dependent Answers'
                : 'Select dependent Answers'}
            </Button>

            <Space />
            <Radio.Group
              options={OPERATORS}
              onChange={onChangeOperator}
              value={group.visibleOperator}
            />

            <Modal
              title={'Select dependent Answers'}
              width={700}
              style={{
                top: 20
              }}
              okText={'Add'}
              footer={null}
              open={isModalOpened}
              // onOk={onAddAnswers}
              onCancel={() => setIsModalOpened(false)}
            >
              {type === 'questionnaire' && (
                <QuestionnaireDependedAnswers
                  dataSortedSections={dataSortedSections}
                  loadingSections={loadingSections}
                  dependentAnswers={dependentAnswers}
                  setDependentAnswers={setDependentAnswers}
                  onAddAnswers={onAddAnswers}
                />
              )}
              {type === 'products' && (
                <ProductDependedAnswers
                  dataSortedSections={dataSortedSections}
                  loadingSections={loadingSections}
                  dependentAnswers={dependentAnswers}
                  setDependentAnswers={setDependentAnswers}
                  onAddAnswers={onAddAnswers}
                />
              )}
            </Modal>
          </>
        )}
        <Space />
        {group.id && (
          <>
            <Form.Item label={t('Order')}>
              <Input
                name='sortOrder'
                value={group.sortOrder}
                style={{ width: '100px' }}
                onChange={onChange}
              />
            </Form.Item>
            <Space />
          </>
        )}
        <Flex>
          <Item>
            <Checkbox
              name='isRequired'
              value={group.isMultiple}
              checked={group.isMultiple}
              onChange={(e) => setGroup({ ...group, isMultiple: e.target.checked })}
            >
              {t('Is Multiple')}
            </Checkbox>
          </Item>

          {group.isMultiple && (
            <Form layout='vertical'>
              <Flex gap={12}>
                <Form.Item label={t('Multiple Min')}>
                  <Input name='multipleMin' value={group.multipleMin} onChange={onChange} />
                </Form.Item>
                <Form.Item label={t('Multiple Max')}>
                  <Input name='multipleMax' value={group.multipleMax} onChange={onChange} />
                </Form.Item>
              </Flex>
            </Form>
          )}
        </Flex>

        <ActionBox>
          <Button
            onClick={handleAction}
            disabled={isDisabled}
            type='primary'
            loading={isCreating || isUpdating}
          >
            {group.id ? t('Update Group') : t('Create Group')}
          </Button>
          {group.id && (
            <Popconfirm
              placement='topRight'
              title={t('Are you sure to delete this group?')}
              onConfirm={onDelete}
              okText={t('Yes')}
              cancelText={t('No')}
            >
              <Button danger loading={isDeleting}>
                {t('Delete Group')}
              </Button>
            </Popconfirm>
          )}
        </ActionBox>
      </ConstructorForm>

      <NestedBox>
        {group.id && <Divider />}
        {!!group.questions?.length && (
          <>
            <QuestionList list={group.questions} type={type} {...props} />
          </>
        )}
        {!!group.productQuestions?.length && (
          <>
            <QuestionList list={group.productQuestions} type={type} />
          </>
        )}

        {group.id && (
          <>
            {showCreateNew ? (
              <>
                {type === 'products' && (
                  <ProductQuestion groupId={group.id} onCreated={() => setShowCreateNew(false)} />
                )}
                {type === 'questionnaire' && (
                  <QuestionnaireQuestion
                    groupId={group.id}
                    {...props}
                    onCreated={() => setShowCreateNew(false)}
                  />
                )}
              </>
            ) : (
              <Button onClick={() => setShowCreateNew(true)}>{t('Add new Question')}</Button>
            )}
          </>
        )}
      </NestedBox>
    </Box>
  );
};

const Item = styled.div`
  width: 150px;
  padding-right: 20px;
`;
